class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.CartDrawer = document.querySelector('.drawer__inner')
    this.cartLinks = document.querySelectorAll('#cart-link');
    this.overlay = document.querySelector('body > .overlay')
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.boundClose = this.close.bind(this);

    // Keep the footer disclosures usable after native section replacements.
    this.addEventListener('keydown', (event) => {
      const summary = event.target.closest('.vorug-cart-footer-options .accordion__summary');
      if (summary && event.code === 'Space' && !summary.closest('.not_collapsible')) {
        event.preventDefault();
        summary.click();
      }
    });
  }

  connectedCallback() {
    this.listenerController = new AbortController();
    this.boundCartLinks = new WeakSet();
    this.overlay = null;
    this.setHeaderCartIconAccessibility();
    const options = { signal: this.listenerController.signal };
    document.addEventListener('shopify:section:load', this.setHeaderCartIconAccessibility.bind(this), options);
    const section = this.closest('#shopify-section-cart-drawer');
    section?.addEventListener('shopify:section:select', this.sectionSelect.bind(this), options);
    section?.addEventListener('shopify:section:deselect', this.boundClose, options);
    this.footerObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList')) this.observeFooterContent();
      this.syncFooterOptions();
    });
    this.footerObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    if (window.ResizeObserver) this.footerResizeObserver = new ResizeObserver(() => this.syncFooterOptions());
    this.observeFooterContent();
    this.syncFooterOptions();
  }

  disconnectedCallback() {
    const wasOpen = this.classList.contains('active') || this.openTimer != null || this.focusTimer != null;
    clearTimeout(this.renderOpenTimer);
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    this.renderOpenTimer = null;
    this.openTimer = null;
    this.focusTimer = null;
    if (wasOpen) {
      const replacementOpen = Array.from(document.querySelectorAll('cart-drawer')).some((drawer) =>
        drawer !== this && (drawer.classList.contains('active') || drawer.openTimer != null || drawer.focusTimer != null)
      );
      if (replacementOpen) this.classList.remove('active');
      else this.close();
    }
    this.listenerController?.abort();
    this.footerObserver?.disconnect();
    this.footerResizeObserver?.disconnect();
  }

  observeFooterContent() {
    this.footerResizeObserver?.disconnect();
    this.querySelectorAll('.vorug-cart-footer-options .accordion__content').forEach((content) => this.footerResizeObserver?.observe(content));
  }

  syncFooterOptions() {
    this.querySelectorAll('.vorug-cart-footer-options accordion-block').forEach((block, index) => {
      const toggle = block.querySelector('.accordion-toggle');
      const summary = block.querySelector('.accordion__summary');
      const panel = block.querySelector('.accordion__panel');
      if (!toggle || !summary || !panel) return;
      const open = toggle.classList.contains('is-open');
      panel.id ||= `CartDrawer-FooterPanel-${index}`;
      panel.inert = !open;
      panel.setAttribute('aria-hidden', String(!open));
      if (!toggle.classList.contains('not_collapsible')) {
        summary.setAttribute('role', 'button');
        summary.setAttribute('aria-controls', panel.id);
        summary.setAttribute('aria-expanded', String(open));
      }
      // The native accordion restores other controls, but omits textareas.
      panel.querySelectorAll('textarea').forEach((textarea) => textarea.setAttribute('tabindex', open ? '0' : '-1'));
      if (open) panel.style.maxHeight = `${panel.scrollHeight}px`;
    });
  }

  setHeaderCartIconAccessibility() {
    if (!this.isConnected || this.listenerController?.signal.aborted) return;
    this.bindOverlay();
    this.cartLinks = document.querySelectorAll('#cart-link');
    Array.from(this.cartLinks).forEach(cartLink => {
      this.boundCartLinks ||= new WeakSet();
      if (this.boundCartLinks.has(cartLink)) return;
      this.boundCartLinks.add(cartLink);
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink)
      }, { signal: this.listenerController.signal });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
          this.open(cartLink);
        }
      }, { signal: this.listenerController.signal });
    })
  }

  bindOverlay() {
    if (!this.isConnected || !this.listenerController || this.listenerController.signal.aborted) return;
    const overlay = document.querySelector('body > .overlay');
    if (overlay === this.overlay) return;
    this.overlay?.removeEventListener('click', this.boundClose);
    this.overlay = overlay;
    this.overlay?.addEventListener('click', this.boundClose, { signal: this.listenerController.signal });
  }

  sectionSelect() {
    this.cartLink = document.querySelector('#cart-link')
    this.open(this.cartLink)
  }

  open(triggeredBy) {
    if (!this.isConnected) return;
    this.bindOverlay();
    if (!this.overlay) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (triggeredBy) this.opener = triggeredBy.querySelector('a.cart');

    if (document.body.classList.contains('quick-view-open')) {
      document.body.classList.remove('hidden', 'quick-view-open', 'quick-view-load')
      let openedQuickView = document.querySelector('.popup-wrapper__quick-view.open')
      if (openedQuickView) {
        openedQuickView.closest('details').removeAttribute('open')
        openedQuickView.closest('.quick-view__content').classList.remove('hide-cover');
        openedQuickView.classList.remove('open')
        openedQuickView.closest('.quick-view__content').innerHTML = '';
      }
    }

    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      if (this.isConnected) this.classList.add('animate', 'active');
    });
    this.overlay.classList.add('open');
    document.body.classList.add('hidden');

    this.CartDrawer = this.querySelector('.drawer__inner');

    const focusTarget =
      this.CartDrawer.querySelector(
        'a[href], button:enabled, input:not([type=hidden]):enabled, select:enabled, textarea:enabled'
      ) ||
      this.querySelector('.drawer__close') ||
      this.CartDrawer;
    clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      this.focusTimer = null;
      if (this.isConnected) trapFocus(this, focusTarget);
    }, 10);

    if (document.querySelector('#shopify-section-menu-drawer .menu-drawer.open') || document.querySelector('#shopify-section-mega-menu-drawer .menu-drawer.open')) {
      document.querySelector('.menu-drawer').setAttribute('hidden', 'true')
      document.querySelector('.menu-drawer').classList.remove('open')
    }

    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  close() {
    clearTimeout(this.renderOpenTimer);
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    this.renderOpenTimer = null;
    this.openTimer = null;
    this.focusTimer = null;
    this.classList.remove('active');
    this.overlay?.classList.remove('open')

    const activeElement = this.activeElement?.isConnected ? this.activeElement : null;
    removeTrapFocus(activeElement?.querySelector('a.cart') || activeElement || (this.opener?.isConnected ? this.opener : null));

    document.body.classList.remove('hidden')
    document.dispatchEvent(new CustomEvent('body:visible'));
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElements = document.querySelectorAll(section.selector);
      if(sectionElements) {
        Array.from(sectionElements).forEach(sectionElement => {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        })
      } 
    }));

    this.setHeaderCartIconAccessibility();
    if(this.className.includes('open-after-adding')) {
      clearTimeout(this.renderOpenTimer);
      this.renderOpenTimer = setTimeout(() => {
        this.renderOpenTimer = null;
        if (!this.isConnected) return;
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    if (document.querySelector('#vn19-site-header, [data-editorial-menu]')) {
      return [{id: 'cart-drawer', selector: '#CartDrawer'}, {id: 'header', selector: '#cart-icon-bubble-header'}];
    }
    let arraySections = []
    if (window.innerWidth > 920 && document.querySelector('.header-without-sidebars')) {
      arraySections = [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer'
        },
        {
          id: 'menu-drawer',
          selector: '#cart-icon-bubble-menu-drawer'
        },
        {
          id: 'header',
          selector: '#cart-icon-bubble-header'
        },
        {
          id: 'secondary-sidebar',
          selector: '#cart-icon-bubble-secondary-sidebar'
        },
        {
          id: 'main-sidebar',
          selector: '#cart-icon-bubble-main-sidebar'
        }
      ];
    } else if (window.innerWidth > 920 && document.querySelector('.secondary-header-section')) {
      arraySections = [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer'
        },
        {
          id: 'menu-drawer',
          selector: '#cart-icon-bubble-menu-drawer'
        },
        {
          id: 'secondary-header',
          selector: '#cart-icon-bubble-secondary-header'
        },
        {
          id: 'secondary-sidebar',
          selector: '#cart-icon-bubble-secondary-sidebar'
        },
        {
          id: 'main-sidebar',
          selector: '#cart-icon-bubble-main-sidebar'
        }
      ];
    } else {
      arraySections = [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer'
        },
        {
          id: 'menu-drawer',
          selector: '#cart-icon-bubble-menu-drawer'
        },
        {
          id: 'mobile-header',
          selector: '#cart-icon-bubble-mobile-header'
        }
      ];
    }
    return arraySections
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);
